import React, { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CatmullRomCurve3 } from 'three';

interface CurveProps {
  points: THREE.Vector3[];
  color?: string;
  opacity?: number;
  thickness?: number;
  randomSeed?: number;
  randomStrengthX?: number;
  randomStrengthY?: number;
  randomStrengthZ?: number;
  launchDelay?: number;
  animationSpeed?: number;
  dissipationStartPercentage?: number;
}

const CustomCurve: React.FC<CurveProps> = ({ 
    points, 
    color = 'white', 
    opacity = 0.3, 
    thickness = 0.1,
    randomSeed = 0,
    randomStrengthX = 0.6,
    randomStrengthY = 0.6,
    randomStrengthZ = 0.6,
    launchDelay = 0,
    animationSpeed = 0.0016,
    dissipationStartPercentage = 0.3
  }) => {
    const tubeRef = useRef<THREE.Mesh>(null);
    const [progress, setProgress] = useState(0);
    const [dissipation, setDissipation] = useState(0);
    const [animationState, setAnimationState] = useState<'waiting' | 'drawing' | 'dissipating' | 'reset'>('waiting');
    const [elapsedTime, setElapsedTime] = useState(0);
  
    const randomizedPoints = useMemo(() => {
      if (!points || points.length < 2) {
        console.error("CustomCurve: points array is empty or invalid");
        return [];
      }
  
      const random = (seed: number) => {
        const x = Math.sin(seed) * 1000;
        return x - Math.floor(x);
      };
  
      return points.map((point, index) => {
        if (!point || !(point instanceof THREE.Vector3)) {
          console.warn(`Invalid point at index ${index}`);
          return new THREE.Vector3(0, 0, 0);
        }
        return new THREE.Vector3(
          point.x + (random(randomSeed + index) - 0.5) * randomStrengthX,
          point.y + (random(randomSeed + index + 1000) - 0.5) * randomStrengthY,
          point.z + (random(randomSeed + index + 2000) - 0.5) * randomStrengthZ
        );
      });
    }, [points, randomSeed, randomStrengthX, randomStrengthY, randomStrengthZ]);
  
    const curve = useMemo(() => {
      if (randomizedPoints.length < 2) return null;
      return new CatmullRomCurve3(randomizedPoints, false, 'catmullrom', 1.1);
    }, [randomizedPoints]);
  
    const totalCurvePoints = useMemo(() => {
      if (!curve) return [];
      const interpolatedPoints: THREE.Vector3[] = [];
      for (let i = 0; i <= 200; i++) {
        const t = i / 200;
        const point = new THREE.Vector3();
        interpolatedPoints.push(curve.getPointAt(t, point));
      }
      return interpolatedPoints;
    }, [curve]);
  
    useFrame((state, delta) => {
      if (!tubeRef.current || totalCurvePoints.length < 2) return;
  
      if (animationState === 'waiting') {
        setElapsedTime(prev => prev + delta);
        if (elapsedTime >= launchDelay) {
          setAnimationState('drawing');
          setElapsedTime(0);
        }
        return;
      }
  
      switch (animationState) {
        case 'reset':
          setProgress(0);
          setDissipation(0);
          setAnimationState('drawing');
          break;
  
        case 'drawing':
          setProgress(prevProgress => {
            const newProgress = prevProgress + animationSpeed;
            if (newProgress >= dissipationStartPercentage) setAnimationState('dissipating');
            return Math.min(newProgress, 1);
          });
          break;
  
        case 'dissipating':
          setProgress(prevProgress => {
            const newProgress = Math.min(prevProgress + animationSpeed, 1);
            setDissipation(prevDissipation => {
              const newDissipation = prevDissipation + animationSpeed;
              if (newDissipation >= 1 || newProgress >= 1) setAnimationState('reset');
              return newDissipation;
            });
            return newProgress;
          });
          break;
      }
  
      const displayPoints = totalCurvePoints.filter((_, index) => {
        const normalizedIndex = index / (totalCurvePoints.length - 1);
        return (
          normalizedIndex <= progress && 
          normalizedIndex >= dissipation
        );
      });
  
      if (displayPoints.length < 2) return;
  
      const tubeGeometry = new THREE.TubeGeometry(
        new CatmullRomCurve3(displayPoints),
        displayPoints.length,
        thickness,
        8,
        false
      );
  
      tubeRef.current.geometry.dispose();
      tubeRef.current.geometry = tubeGeometry;
    });
  
    return (
      <mesh ref={tubeRef}>
        <tubeGeometry />
        <meshBasicMaterial color={color} transparent opacity={opacity} />
      </mesh>
    );
  };

const WindStreaks = () => {
  const points1 = [
    new THREE.Vector3(4.18853, 4.5, 36.4286),
    new THREE.Vector3(-13.0909, 5.2, 36.129),
    new THREE.Vector3(-12.614, 6.5, 27.7502),
    new THREE.Vector3(-20.3862, 8.5, 23.9809),
    new THREE.Vector3(-30.3862, 9, 20),
  ];

  const points2 = [
    new THREE.Vector3(26.9919, 18.3536, -29.9346),
    new THREE.Vector3(15.2242, 6.71621, -18.0143),
    new THREE.Vector3(-7.42068, 5.36614, -16.4473),
    new THREE.Vector3(-19.0747, 8.26793, -20.4468),
    new THREE.Vector3(-28.851, 10.6919, -28.6407),
  ];


  return (
    <>
      {[...Array(3)].map((_, index) => (
        <CustomCurve 
          key={index}
          points={points1}
          color="white"
          opacity={0.3}
          thickness={0.06} 
          randomSeed={index}
          randomStrengthX={0.5 * (index + 1)}
          randomStrengthY={3 * (index + 1)}
          randomStrengthZ={0.8 * (index + 1)}
          launchDelay={5 + (1.5 * index)}
          animationSpeed={0.0025}
          dissipationStartPercentage={0.25} 
        />
      ))}
      {[...Array(4)].map((_, index) => (
        <CustomCurve 
          key={index}
          points={points2}
          color="white"
          opacity={0.3}
          thickness={0.06} 
          randomSeed={index}
          randomStrengthX={4 * (index + 1)}
          randomStrengthY={2 * (index + 1)}
          randomStrengthZ={5 * (index + 1)}
          launchDelay={4 + (3 * index)}
          animationSpeed={0.002}
          dissipationStartPercentage={0.15}
        />
      ))}
    </>
  );
};

export default WindStreaks;